/**
 * Tableau de bord technicien : vue d'ensemble de ses dossiers + RDV à
 * venir + raccourcis vers ses sous-routes.
 */
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, ClipboardList, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import {
  listTechnicianAppointments,
  listTechnicianProjects,
  listTechnicianReports,
} from '@/features/admin/admin-api';
import { listAssignedAppointments } from '@/features/appointments/appointments-api';
import { useAuth } from '@/lib/auth-context';

export function TechnicianOverviewPage() {
  const { user } = useAuth();
  const projects = useQuery({
    queryKey: ['technician', 'projects'],
    queryFn: listTechnicianProjects,
  });
  const appointments = useQuery({
    queryKey: ['technician', 'appointments'],
    queryFn: listTechnicianAppointments,
  });
  const reports = useQuery({
    queryKey: ['technician', 'reports'],
    queryFn: listTechnicianReports,
  });
  const assigned = useQuery({
    queryKey: ['technician', 'assigned'],
    queryFn: listAssignedAppointments,
  });
  const toRespond = (assigned.data ?? []).filter((a) => a.status === 'attribue');

  const upcoming = (appointments.data ?? []).filter(
    (a) => new Date(a.scheduledAt).getTime() >= Date.now(),
  );

  const tiles = [
    {
      title: 'Mes dossiers',
      value: projects.data?.length ?? '—',
      description: 'Dossiers qui me sont affectés',
      icon: ClipboardList,
      to: '/technicien/projects',
    },
    {
      title: 'RDV à venir',
      value: upcoming.length,
      description: 'Interventions planifiées',
      icon: CalendarClock,
      to: '/technicien/planning',
    },
    {
      title: 'Comptes rendus',
      value: reports.data?.length ?? '—',
      description: 'Rapports rédigés',
      icon: FileText,
      to: '/technicien/reports',
    },
  ];

  return (
    <DashboardLayout variant="technicien">
      {toRespond.length > 0 && (
        <Link to="/technicien/projects" className="mb-6 block">
          <Card className="border-2 border-amber-400 bg-amber-50 transition hover:shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
                </span>
                {toRespond.length} dossier{toRespond.length > 1 ? 's' : ''} à répondre
              </CardTitle>
              <CardDescription>
                Un rendez-vous vous a été affecté : ouvrez le dossier dans « Mes
                dossiers » pour l'accepter ou le refuser (avec un motif).
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Bienvenue, {user?.firstName}</CardTitle>
          <CardDescription>
            Voici un récapitulatif de votre activité du jour.
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {tiles.map((tile) => (
          <Link key={tile.title} to={tile.to} className="group block">
            <Card className="transition-shadow group-hover:shadow-md">
              <CardHeader className="flex flex-row items-start gap-3 pb-3">
                <div className="grid h-10 w-10 place-items-center rounded-md bg-bcl-blue/10 text-bcl-blue">
                  <tile.icon className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-sm font-medium text-gray-500">
                    {tile.title}
                  </CardTitle>
                  <p className="mt-1 text-2xl font-bold">{tile.value}</p>
                </div>
              </CardHeader>
              <CardContent className="pt-0 text-xs text-gray-500">
                {tile.description}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Prochains rendez-vous</CardTitle>
        </CardHeader>
        <CardContent>
          {upcoming.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun rendez-vous à venir.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {upcoming.slice(0, 5).map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between rounded-md border bg-white p-3"
                >
                  <span className="font-medium">
                    {new Date(a.scheduledAt).toLocaleString('fr-FR')}
                  </span>
                  <span className="text-xs text-gray-500">
                    Projet {a.projectId} · {a.durationMinutes} min
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
